/**
 * Claves de `permisos_campos_json` por etapa (mismo esquema que crear usuario).
 * Cada etapa es un listado de claves que el usuario puede editar en gestión.
 */
export const GESTION_ETAPA_KEYS = [
  "solicitud_servicio",
  "factibilidad_servicio",
  "revision_aprobacion",
  "ejecucion_obras",
  "puesta_servicio",
] as const;

export type GestionEtapaKey = (typeof GESTION_ETAPA_KEYS)[number];

export const GESTION_ETAPA_LABELS: Record<GestionEtapaKey, string> = {
  solicitud_servicio: "Solicitud del servicio",
  factibilidad_servicio: "Diseño y factibilidad",
  revision_aprobacion: "Supervisión y aprobación",
  ejecucion_obras: "Ejecución de obras",
  puesta_servicio: "Puesta en servicio",
};

const solicitudCampos: Array<{ key: string; label: string }> = [
  { key: "codigo", label: "Código" },
  { key: "nombre", label: "Nombre del proyecto" },
  { key: "tipo_proyecto", label: "Tipo de proyecto" },
  { key: "nivel_tension", label: "Nivel de tensión" },
  { key: "fecha_estimada_operacion", label: "Fecha estimada de operación" },
  { key: "numero_usuarios", label: "Número de usuarios" },
  { key: "carga_total_solicitada_kva", label: "Carga total solicitada (kVA)" },
  { key: "tipo_subestacion", label: "Tipo de subestación" },
  { key: "nodo_referencia", label: "Nodo de referencia" },
  { key: "nombre_solicitante", label: "Nombre del solicitante" },
  { key: "tipo_documento", label: "Tipo de documento" },
  { key: "documento_solicitante", label: "Documento" },
  { key: "tipo_persona", label: "Tipo de persona" },
  { key: "tipo_persona_juridica", label: "Tipo de persona jurídica" },
  { key: "correo_notificacion", label: "Correo" },
  { key: "telefono_solicitante", label: "Celular" },
  { key: "telefono_fijo", label: "Teléfono fijo" },
  { key: "predio_departamento", label: "Departamento del predio" },
  { key: "predio_municipio", label: "Municipio del predio" },
  { key: "predio_comuna", label: "Comuna del predio" },
  { key: "direccion_predio", label: "Dirección del predio" },
  { key: "latitud", label: "Latitud" },
  { key: "longitud", label: "Longitud" },
  { key: "magna_este", label: "Magna este" },
  { key: "magna_norte", label: "Magna norte" },
  { key: "observaciones", label: "Observaciones" },
  { key: "responsable_emcali", label: "Responsable EMCALI" },
  { key: "fecha_radicado", label: "Fecha de solicitud" },
  { key: "revision_observaciones_solicitud", label: "Observaciones de revisión (solicitud)" },
  { key: "documentos_solicitud", label: "Documentos de solicitud" },
  { key: "acciones_aprobacion_solicitud", label: "Aprobar / devolver / rechazar solicitud" },
  { key: "gestion_documental", label: "Gestión documental de diseño (checklist conexiones complejas)" },
  { key: "revision_documental_factibilidad", label: "Revisión documental (cédula / uso especial)" },
];

const factibilidadCampos: Array<{ key: string; label: string }> = [
  { key: "flujo_estado", label: "Flujo" },
  { key: "proceso_estado", label: "Estado del proceso" },
  { key: "numero_upm", label: "Número UPM" },
  { key: "nodo_referencia", label: "Nodo referencia" },
  { key: "nodo_conexion", label: "Nodo conexión" },
  { key: "observaciones", label: "Observaciones factibilidad" },
  { key: "resultado_diseno", label: "Resultado de diseño (aprobado/devuelto)" },
  { key: "resena_revision_diseno", label: "Reseña de revisión de diseño" },
  { key: "plano_diseno", label: "Plano de diseño (carga/visualización)" },
  { key: "presupuesto_diseno", label: "Presupuesto de diseño (carga/visualización)" },
  { key: "matriz_diseno", label: "Matriz ítems de diseño" },
  { key: "decision_revision_diseno", label: "Decisión y observación revisión" },
  { key: "historial_revisiones_diseno", label: "Historial de revisiones de diseño" },
  { key: "guardar_factibilidad", label: "Guardar factibilidad / aprobar factibilidad" },
  { key: "documentos_factibilidad", label: "Documentos de solicitud y factibilidad" },
  { key: "gestion_documental", label: "Gestión documental de diseño (checklist conexiones complejas)" },
  { key: "revision_documental_factibilidad", label: "Revisión documental (cédula / uso especial)" },
];

const revisionCampos: Array<{ key: string; label: string }> = [
  { key: "proceso_estado", label: "Estado del proceso" },
  { key: "documentos_supervision", label: "Documentos de supervisión" },
  { key: "matriz_supervision", label: "Estructura base de supervisión" },
  { key: "acta_terreno", label: "Acta levantada en terreno" },
  { key: "decision_supervision", label: "Decisiones de supervisión" },
  { key: "registrar_trazabilidad_supervision", label: "Registrar trazabilidad de supervisión" },
  { key: "historial_supervision", label: "Historial de supervisión" },
];

const ejecucionCampos: Array<{ key: string; label: string }> = [
  { key: "proceso_estado", label: "Estado del proceso" },
  { key: "documentos_ejecucion", label: "Documentos de ejecución" },
  { key: "matriz_ejecucion", label: "Estructura base de ejecución" },
  { key: "soporte_acta_visita", label: "Soporte acta de visita" },
  { key: "decision_ejecucion", label: "Decisión y observación ejecución" },
  { key: "registrar_trazabilidad_ejecucion", label: "Registrar trazabilidad de ejecución" },
  { key: "historial_ejecucion", label: "Historial de ejecución" },
];

const puestaMetricasKeys = [
  "revision_documentos",
  "visita_previa",
  "aviso_macromedicion",
  "nuevos_servicios",
  "devolucion",
  "correccion",
  "maniobra_ejecucion",
  "entrega_equipos_contratista",
  "cuadrilla",
  "instalacion",
  "enviar_soportes",
  "seguimiento",
  "medidor_legalizado",
  "facturado",
] as const;

const puestaCampos: Array<{ key: string; label: string }> = [
  { key: "tipo_solicitud", label: "Tipo de solicitud" },
  { key: "nombre", label: "Nombre" },
  { key: "fecha_maniobra", label: "Fecha de maniobra" },
  { key: "fecha_fin_estimada", label: "Fin estimada" },
  { key: "fecha_recepcion_correo", label: "Recepción correo" },
  { key: "fecha_recepcion_fisica", label: "Recepción física" },
  { key: "fecha_maniobra_comite", label: "Maniobra comité" },
  { key: "maniobra_final_fecha", label: "Maniobra final fecha" },
  { key: "dias_entrega_fisica_vs_comite", label: "Días entrega física vs comité" },
  { key: "macromedicion", label: "Macromedición" },
  { key: "fecha_instalacion_macromedicion", label: "Fecha instalación macromedición" },
  { key: "contrato_macromedicion", label: "Contrato macromedición" },
  { key: "estado", label: "Estado" },
  { key: "proceso_estado", label: "Proceso estado" },
  { key: "alerta", label: "Alerta" },
  { key: "link_carpeta", label: "Link de carpeta" },
  { key: "observacion", label: "Observación" },
  { key: "avance_porcentaje", label: "Porcentaje de avance" },
  ...puestaMetricasKeys.map((k) => ({
    key: k,
    label: `Indicador: ${k.replace(/_/g, " ")}`,
  })),
  { key: "guardar_puesta_servicio", label: "Guardar puesta en servicio" },
];

export const PERMISOS_CAMPOS_POR_ETAPA: Record<GestionEtapaKey, Array<{ key: string; label: string }>> = {
  solicitud_servicio: solicitudCampos,
  factibilidad_servicio: factibilidadCampos,
  revision_aprobacion: revisionCampos,
  ejecucion_obras: ejecucionCampos,
  puesta_servicio: puestaCampos,
};

export function permisosCamposVacios(): Record<GestionEtapaKey, string[]> {
  return {
    solicitud_servicio: [],
    factibilidad_servicio: [],
    revision_aprobacion: [],
    ejecucion_obras: [],
    puesta_servicio: [],
  };
}

export function parsePermisosCamposJson(raw: string | null | undefined): Record<GestionEtapaKey, string[]> {
  const base = permisosCamposVacios();
  if (!raw || raw === "{}") return base;
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== "object") return base;
    for (const k of GESTION_ETAPA_KEYS) {
      const arr = obj[k];
      if (Array.isArray(arr)) {
        base[k] = arr.map((x) => String(x)).filter(Boolean);
      }
    }
    return base;
  } catch {
    return base;
  }
}
